import type { NormalizedEmailAssessment, Parent, StatusCopy } from "../types";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const getPortalStatus = (parent: Pick<Parent, "accountEnabled" | "emailVerified"> | null | undefined, t: Translate): StatusCopy => {
  if (!parent?.accountEnabled) {
    return { label: t("common.inactive", { defaultValue: "Inactive" }), tone: "danger", cardTone: "danger" };
  }
  if (parent.emailVerified) {
    return { label: t("common.active", { defaultValue: "Active" }), tone: "success", cardTone: "success" };
  }
  return { label: t("common.pending", { defaultValue: "Pending" }), tone: "warn", cardTone: "warn" };
};

export const getNewsletterStatus = (
  parent: Pick<Parent, "newsletterOptOut" | "newsletterStatus"> | null | undefined,
  t: Translate
): StatusCopy => {
  if (parent?.newsletterOptOut) {
    return {
      label: t("family.parents.newsletter.opted_out", { defaultValue: "Opted out" }),
      tone: "warn",
      cardTone: "warn",
      title: t("family.parents.newsletter.opted_out_detail", { defaultValue: "Parent has opted out" }),
    };
  }
  if (parent?.newsletterStatus === "SUBSCRIBED") {
    return {
      label: t("family.parents.newsletter.subscribed", { defaultValue: "Subscribed" }),
      tone: "success",
      cardTone: "success",
      title: "SUBSCRIBED",
    };
  }
  if (parent?.newsletterStatus === "UNSUBSCRIBED") {
    return {
      label: t("family.parents.newsletter.unsubscribed", { defaultValue: "Unsubscribed" }),
      tone: "warn",
      cardTone: "warn",
      title: "UNSUBSCRIBED",
    };
  }
  return {
    label: t("family.parents.newsletter.ineligible", { defaultValue: "Ineligible" }),
    tone: "neutral",
    cardTone: "neutral",
    title: parent?.newsletterStatus || t("family.parents.newsletter.ineligible", { defaultValue: "Ineligible" }),
  };
};

export const getBackendEmailAssessment = (parent: Parent): NormalizedEmailAssessment | null => {
  const assessment = parent.emailValidation || parent.emailAssessment || parent.emailCheck || null;
  if (!assessment || typeof assessment !== "object") return null;

  const rawStatus = assessment.status ?? assessment.kind ?? assessment.level ?? null;
  const status = rawStatus != null ? String(rawStatus).trim().toLowerCase() : "";
  const normalizedStatus =
    status === "valid" || status === "ok"
      ? "valid"
      : status === "invalid" || status === "error"
        ? "invalid"
        : status === "suspicious" || status === "warning" || status === "warn"
          ? "suspicious"
          : null;
  if (!normalizedStatus) return null;

  const reasons = Array.isArray(assessment.reasons)
    ? assessment.reasons.map((reason) => String(reason)).filter(Boolean)
    : [];
  const suggestion =
    assessment.suggestedEmail != null
      ? String(assessment.suggestedEmail)
      : assessment.suggestion != null
        ? String(assessment.suggestion)
        : null;
  const source = assessment.source != null ? String(assessment.source) : "backend";

  return { status: normalizedStatus, reasons, suggestion, source };
};
